import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { pixelateImage, generateColorMapping, generateMatchedPixelData } from '@pindd/core'
import { useProjectStore } from '../store/projectStore'
import { PixelData, BoardSize, ColorMapping } from '@pindd/types'
import { Button } from '../components/ui/button'
import { Footer } from '../components/Footer'
import { ArrowRight, Grid3X3, Image as ImageIcon, Eye, Circle, Square } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fiveMmBoards, twoSixMmBoards, perlerColors } from '@pindd/data'

export function Pixelate() {
  const navigate = useNavigate()
  const { project, updatePixelatedData, updateBoardSize, updateColorMapping, updatePixelShape } = useProjectStore()
  const [pixelData, setPixelData] = useState<PixelData | null>(null)
  const [width, setWidth] = useState(25) // Default to 2.6mm small
  const [height, setHeight] = useState(25)
  const [selectedBoard, setSelectedBoard] = useState<BoardSize>(twoSixMmBoards[0]) // Default 2.6mm
  const [showOriginal, setShowOriginal] = useState(false)
  const [colorMappings, setColorMappings] = useState<ColorMapping[]>([])
  const [pixelShape, setPixelShape] = useState<'round' | 'square'>('round')
  const [matchedPixels, setMatchedPixels] = useState<string[][]>([])
  const [colorPrecision, setColorPrecision] = useState<number>(32)

  // Pixelation with proper cleanup
  const pixelationIdRef = useRef(0)

  useEffect(() => {
    if (!project?.sourceImage) return

    const currentId = ++pixelationIdRef.current

    requestAnimationFrame(() => {
      const img = new window.Image()
      img.onload = () => {
        if (currentId !== pixelationIdRef.current) return

        const data = pixelateImage(img, width, height)

        if (currentId !== pixelationIdRef.current) return

        setPixelData(data)
        updatePixelatedData(data)

        // Use precision parameter for color mapping
        const mappings = generateColorMapping(data, perlerColors, colorPrecision)
        setColorMappings(mappings)

        // Generate matched pixel data for accurate preview
        const matched = generateMatchedPixelData(data, perlerColors, colorPrecision)
        setMatchedPixels(matched)
      }
      img.src = project.sourceImage
    })
  }, [project?.sourceImage, width, height, colorPrecision])

  const handleSelectBoard = (board: BoardSize) => {
    setSelectedBoard(board)
    setWidth(board.width)
    setHeight(board.height)
  }

  const handleProceed = () => {
    updateBoardSize(selectedBoard)
    updateColorMapping(colorMappings)
    updatePixelShape(pixelShape)
    navigate('/create')
  }

  const usedColors = colorMappings.map(m => ({
    color: m.matchedColor,
    count: m.count,
  }))

  const totalBeads = usedColors.reduce((sum, c) => sum + c.count, 0)

  // Calculate preview cell size
  const getPreviewCellSize = () => {
    if (typeof window === 'undefined') return 16
    const maxWidth = Math.min(window.innerWidth * 0.55, 600)
    return Math.max(8, Math.floor(maxWidth / Math.max(width, height)))
  }
  const previewCellSize = getPreviewCellSize()

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="container flex h-14 items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            返回
          </Button>
          <h1 className="font-semibold">像素化设置</h1>
          <Button size="sm" onClick={handleProceed}>
            开始创作
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 container py-4">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 h-full">
          {/* Preview Area */}
          <div className="flex items-center justify-center bg-muted/30 rounded-xl p-4 min-h-[400px]">
            {showOriginal && project?.sourceImage ? (
              <div
                className="max-w-full max-h-[60vh] overflow-hidden rounded-lg border"
                style={{ aspectRatio: `${width}/${height}` }}
              >
                <img
                  src={project.sourceImage}
                  alt="原图"
                  className="w-full h-full object-cover"
                />
              </div>
            ) : pixelData ? (
              <div className="relative">
                {/* Grid numbers - Top */}
                <div className="flex" style={{ paddingLeft: previewCellSize }}>
                  {Array.from({ length: pixelData.width }).map((_, x) => (
                    <div
                      key={x}
                      className="text-[10px] text-muted-foreground text-center"
                      style={{ width: previewCellSize }}
                    >
                      {x % 5 === 0 ? x : ''}
                    </div>
                  ))}
                </div>

                <div className="flex">
                  {/* Grid numbers - Left */}
                  <div className="flex flex-col">
                    {Array.from({ length: pixelData.height }).map((_, y) => (
                      <div
                        key={y}
                        className="text-[10px] text-muted-foreground flex items-center justify-end pr-1"
                        style={{ height: previewCellSize, width: previewCellSize }}
                      >
                        {y % 5 === 0 ? y : ''}
                      </div>
                    ))}
                  </div>

                  {/* Pixels */}
                  <div className="border border-border/50 rounded bg-white">
                    {(matchedPixels.length > 0 ? matchedPixels : pixelData.pixels).map((row, y) => (
                      <div key={y} className="flex">
                        {row.map((hex, x) => {
                          const normalizedHex = hex.toLowerCase()
                          const color = perlerColors.find(c => c.hex.toLowerCase() === normalizedHex)
                          return (
                            <div
                              key={`${x}-${y}`}
                              className={pixelShape === 'round' ? 'rounded-full' : 'rounded-sm'}
                              style={{
                                width: previewCellSize - 1,
                                height: previewCellSize - 1,
                                backgroundColor: color?.hex || hex,
                                margin: 0.5,
                              }}
                              title={color ? `${color.code} - ${color.name}` : hex}
                            />
                          )
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground">正在生成预览...</div>
            )}
          </div>

          {/* Settings Panel */}
          <div className="space-y-4">
            {/* Show Original Toggle */}
            <button
              onClick={() => setShowOriginal(!showOriginal)}
              className={cn(
                "w-full flex items-center gap-2 p-3 rounded-lg border transition-all",
                showOriginal
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-muted-foreground/50"
              )}
            >
              {showOriginal ? (
                <Eye className="h-4 w-4 text-primary" />
              ) : (
                <ImageIcon className="h-4 w-4" />
              )}
              <span className="text-sm font-medium">
                {showOriginal ? '查看像素化' : '查看原图'}
              </span>
            </button>

            {/* Pixel Shape Toggle */}
            <div className="p-3 rounded-lg border bg-card">
              <p className="text-xs text-muted-foreground mb-2">像素形状</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPixelShape('round')}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 p-2 rounded-lg border transition-all text-sm",
                    pixelShape === 'round'
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/50"
                  )}
                >
                  <Circle className="h-4 w-4" />
                  <span>圆形</span>
                </button>
                <button
                  onClick={() => setPixelShape('square')}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 p-2 rounded-lg border transition-all text-sm",
                    pixelShape === 'square'
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/50"
                  )}
                >
                  <Square className="h-4 w-4" />
                  <span>方形</span>
                </button>
              </div>
            </div>

            {/* Color Precision Control */}
            <div className="p-4 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">颜色精度</span>
                <span className="text-xs text-muted-foreground">{colorPrecision}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mb-3">
                值越小颜色越细腻，值越大颜色越统一
              </p>
              <input
                type="range"
                min="16"
                max="64"
                step="8"
                value={colorPrecision}
                onChange={(e) => setColorPrecision(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>细腻</span>
                <span>统一</span>
              </div>
            </div>

            {/* Board Size Selection */}
            <div className="p-4 rounded-lg border bg-card">
              <div className="flex items-center gap-2 mb-3">
                <Grid3X3 className="h-4 w-4" />
                <span className="text-sm font-medium">画板尺寸</span>
              </div>

              <div className="space-y-3">
                {/* 2.6mm */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">2.6mm 拼豆</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {twoSixMmBoards.map((board) => (
                      <button
                        key={board.id}
                        onClick={() => handleSelectBoard(board)}
                        className={cn(
                          "flex flex-col items-center p-2 rounded-md border text-xs transition-all",
                          selectedBoard.id === board.id
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/50"
                        )}
                      >
                        <span className="font-medium">{board.name}</span>
                        <span className="text-muted-foreground">{board.physicalWidth}mm</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 5mm */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">5mm 拼豆</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {fiveMmBoards.map((board) => (
                      <button
                        key={board.id}
                        onClick={() => handleSelectBoard(board)}
                        className={cn(
                          "flex flex-col items-center p-2 rounded-md border text-xs transition-all",
                          selectedBoard.id === board.id
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/50"
                        )}
                      >
                        <span className="font-medium">{board.name}</span>
                        <span className="text-muted-foreground">{board.physicalWidth}mm</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Current Selection Info */}
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">当前选择</span>
                <span className="font-medium">{selectedBoard.name}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">物理尺寸</span>
                <span>{selectedBoard.physicalWidth}×{selectedBoard.physicalHeight}mm</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">像素数</span>
                <span>{width}×{height}</span>
              </div>
            </div>

            {/* Start Button */}
            <Button className="w-full" size="lg" onClick={handleProceed}>
              开始创作
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom Color Summary */}
      {usedColors.length > 0 && (
        <div className="border-t bg-card">
          <div className="container py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">颜色统计</h3>
              <span className="text-sm text-muted-foreground">
                共 <span className="font-medium text-foreground">{usedColors.length}</span> 种颜色，
                <span className="font-medium text-foreground">{totalBeads}</span> 个拼豆
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {usedColors.map(({ color, count }) => (
                <div
                  key={color.id}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border bg-background"
                >
                  <div
                    className="w-4 h-4 rounded-full border border-black/10 flex-shrink-0"
                    style={{ backgroundColor: color.hex }}
                  />
                  <span className="text-xs font-medium">{color.code}</span>
                  <span className="text-xs text-muted-foreground">×{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  )
}
